import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import useReveal from '@/hooks/useReveal';

const values = [
  { icon: 'ri-shield-check-line', title: 'Integrity', desc: 'Transparent communication. No hidden costs, no over-promises.' },
  { icon: 'ri-lightbulb-flash-line', title: 'Innovation', desc: 'AI-first thinking. We explore what is next, not what was last year.' },
  { icon: 'ri-focus-3-line', title: 'Precision', desc: 'Every pixel, every line, every decision serves the outcome.' },
  { icon: 'ri-speed-line', title: 'Velocity', desc: 'Fast iterations. Ship early, learn faster, optimize constantly.' },
  { icon: 'ri-team-line', title: 'Partnership', desc: 'Your team, extended. We build relationships, not vendor contracts.' },
  { icon: 'ri-vip-diamond-line', title: 'Craft', desc: 'Engineering as art. Clean architecture, beautiful interfaces.' },
];

const stats = [
  { v: '120+', l: 'Projects Delivered' },
  { v: '45+', l: 'AI Agents Deployed' },
  { v: '35+', l: 'E-commerce Stores' },
  { v: '98%', l: 'Client Retention' },
];

const timeline = [
  { year: '2019', title: 'Founded', desc: 'FormaCode started as a small team of engineers and designers building web apps for local startups.' },
  { year: '2020', title: 'First AI Project', desc: 'Launched our first AI-powered automation system for a logistics client, cutting their processing time by 70%.' },
  { year: '2021', title: 'Global Expansion', desc: 'Expanded to serve clients across 12 countries, from SaaS startups to Fortune 500 enterprises.' },
  { year: '2022', title: 'AI-First Pivot', desc: 'Shifted to an AI-first approach — integrating LLMs, agents, and automation into every project.' },
  { year: '2023', title: 'Platform Era', desc: 'Built proprietary platforms for e-commerce, analytics, and automation serving 50K+ monthly users.' },
  { year: '2024', title: 'Enterprise Scale', desc: 'Partnered with enterprise clients for mission-critical digital infrastructure and AI transformation.' },
];

function AnimatedCounter({ target, label }: { target: string; label: string }) {
  return (
    <div className="text-center reveal">
      <div className="font-display text-4xl sm:text-5xl md:text-6xl font-bold text-gradient">{target}</div>
      <div className="mt-1.5 text-xs sm:text-sm text-white/50">{label}</div>
    </div>
  );
}

export default function AboutPage() {
  useReveal();
  return (
    <div className="bg-ink-950 text-white min-h-screen">
      <Navbar />

      {/* HERO */}
      <section className="relative pt-[clamp(105px,8.5vw,135px)] pb-12 sm:pb-16 overflow-hidden w-full">
        <img
          src="https://readdy.ai/api/search-image?query=3D%20rendered%20abstract%20holographic%20technology%20scene%20with%20floating%20translucent%20geometric%20shapes%20and%20glowing%20purple%20neon%20data%20streams%20in%20deep%20dark%20space%2C%20soft%20violet%20and%20cyan%20haze%2C%20glossy%20isometric%20render%20style%2C%20floating%20holographic%20interface%20elements%2C%20premium%20cinematic%20atmosphere&width=1800&height=1200&seq=fc-ab2-hero&orientation=landscape"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-ink-950/80 via-ink-950/70 to-ink-950" />
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 md:px-8 w-full">
          <div className="max-w-3xl">
            <div className="text-xs font-mono tracking-widest text-neon-400">// ABOUT US</div>
            <h1 className="mt-4 font-display text-4xl sm:text-6xl md:text-7xl font-bold leading-[1.08] tracking-tight text-gradient">
              We Build Technology<br />That Moves Businesses Forward.
            </h1>
            <p className="mt-4 text-base sm:text-lg text-white/70 max-w-xl leading-relaxed">
              FormaCode Technologies is a modern digital studio blending engineering, AI and design to deliver products that scale.
            </p>
          </div>
        </div>
      </section>

      {/* MISSION + VISION */}
      <section className="relative py-12 sm:py-16 md:py-24 px-4 sm:px-6">
        <div className="mx-auto max-w-7xl grid md:grid-cols-2 gap-10 md:gap-14 items-center">
          <div className="reveal">
            <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-bold text-gradient">Our Mission</h2>
            <p className="mt-4 text-white/70 leading-relaxed text-base sm:text-lg">
              To empower businesses with intelligent digital solutions that reduce costs, accelerate growth, and create competitive advantage through technology.
            </p>
            <h2 className="mt-8 font-display text-2xl sm:text-3xl md:text-4xl font-bold text-gradient">Our Vision</h2>
            <p className="mt-4 text-white/70 leading-relaxed text-base sm:text-lg">
              A world where every business — regardless of size — has access to enterprise-grade technology, AI automation, and digital experiences that rival the world's best.
            </p>
          </div>
          <div className="reveal relative">
            <div className="absolute -inset-6 rounded-3xl bg-neon-500/20 blur-3xl" />
            <img
              src="https://readdy.ai/api/search-image?query=3D%20rendered%20holographic%20geometric%20crystal%20structure%20with%20floating%20translucent%20cubes%20and%20digital%20wireframe%20architecture%20in%20deep%20dark%20purple%20space%2C%20soft%20violet%20and%20cyan%20neon%20glow%20inside%20the%20crystal%2C%20glossy%20isometric%20render%20style%2C%20floating%20holographic%20interface%20panels%2C%20premium%20technology%20aesthetic%2C%20clean%20minimal%20composition&width=1200&height=1000&seq=fc-ab2-mission&orientation=portrait"
              alt="Vision"
              className="relative rounded-2xl w-full max-h-96 md:max-h-full object-cover border border-white/10"
            />
          </div>
        </div>
      </section>

      {/* TIMELINE */}
      <section className="relative py-12 sm:py-16 md:py-24 px-4 sm:px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-ink-900/40 via-transparent to-ink-950" />
        <div className="relative mx-auto max-w-5xl">
          <div className="text-center reveal">
            <div className="text-xs font-mono tracking-widest text-neon-400">// OUR JOURNEY</div>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl md:text-5xl font-bold text-gradient">The Story So Far</h2>
          </div>
          <div className="mt-12 sm:mt-16 relative">
            <div className="absolute left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-neon-500/0 via-neon-500/40 to-neon-500/0 hidden md:block" />
            {timeline.map((item, i) => (
              <div key={item.year} className={`reveal relative flex flex-col md:flex-row items-center gap-6 md:gap-0 mb-8 sm:mb-12 last:mb-0 ${i % 2 === 0 ? '' : 'md:flex-row-reverse'}`}>
                <div className={`w-full md:w-1/2 ${i % 2 === 0 ? 'md:text-right md:pr-12' : 'md:text-left md:pl-12'}`}>
                  <div className="glass rounded-xl p-5 sm:p-6 border border-white/5 hover:border-neon-500/30 transition">
                    <div className="font-mono text-neon-400 text-xs sm:text-sm font-bold">{item.year}</div>
                    <h3 className="mt-1.5 font-display text-lg sm:text-xl font-bold text-white">{item.title}</h3>
                    <p className="mt-2 text-xs sm:text-sm text-white/60 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
                <div className="absolute left-1/2 -translate-x-1/2 hidden md:flex w-4 h-4 rounded-full bg-neon-500 border-4 border-ink-950 z-10 items-center justify-center">
                  <div className="w-1.5 h-1.5 rounded-full bg-white" />
                </div>
                <div className="w-full md:w-1/2" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="relative py-12 sm:py-16 md:py-24 px-4 sm:px-6 overflow-hidden">
        <div className="relative mx-auto max-w-7xl">
          <div className="text-center reveal">
            <div className="text-xs font-mono tracking-widest text-neon-400">// VALUES</div>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl md:text-5xl font-bold text-gradient">What We Stand For</h2>
          </div>
          <div className="mt-10 sm:mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {values.map((v, i) => (
              <div key={v.title} className="reveal glass rounded-xl p-6 sm:p-7 hover:border-neon-500/50 transition" style={{ transitionDelay: `${i * 50}ms` }}>
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-gradient-to-br from-neon-600 to-neon-400 text-white text-lg">
                  <i className={v.icon} />
                </div>
                <h3 className="mt-4 font-display font-semibold text-base sm:text-lg text-white">{v.title}</h3>
                <p className="mt-2 text-xs sm:text-sm text-white/60 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="relative py-16 sm:py-24 px-4 sm:px-6">
        <div className="absolute inset-0 bg-gradient-to-b from-ink-800/40 to-ink-950" />
        <div className="relative mx-auto max-w-7xl grid grid-cols-2 md:grid-cols-4 gap-8">
          {stats.map((s) => (
            <AnimatedCounter key={s.l} target={s.v} label={s.l} />
          ))}
        </div>
      </section>

      {/* WHY FORMACODE */}
      <section className="relative py-12 sm:py-16 md:py-24 px-4 sm:px-6">
        <div className="mx-auto max-w-7xl">
          <div className="max-w-2xl reveal">
            <div className="text-xs font-mono tracking-widest text-neon-400">// WHY FORMACODE</div>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl md:text-5xl font-bold text-gradient leading-tight">We Engineer, Not Assemble</h2>
          </div>
          <div className="mt-10 sm:mt-14 grid md:grid-cols-2 gap-8 md:gap-10">
            <div className="reveal space-y-4">
              <p className="text-white/70 leading-relaxed text-base sm:text-lg">
                Most agencies deliver templates with your logo on top. We build from first principles — clean architecture, type-safe code, AI-native features, and performance-first engineering.
              </p>
              <p className="text-white/60 leading-relaxed text-sm sm:text-base">
                Our team brings together full-stack engineers, AI specialists, SEO strategists, and motion designers under one roof. That means every project gets technical depth plus visual polish, without the handoff gaps.
              </p>
            </div>
            <div className="reveal relative">
              <div className="absolute -inset-4 rounded-3xl bg-neon-500/15 blur-2xl" />
              <img
                src="https://readdy.ai/api/search-image?query=3D%20rendered%20holographic%20geometric%20shapes%20floating%20in%20deep%20dark%20purple%20space%20with%20subtle%20silver%20and%20purple%20neon%20glow%2C%20translucent%20crystal%20polyhedrons%20and%20wireframe%20spheres%20orbiting%2C%20glossy%20isometric%20render%20style%2C%20floating%20holographic%20UI%20elements%2C%20premium%20technology%20aesthetic%2C%20clean%20minimal%20composition&width=1200&height=900&seq=fc-ab2-why&orientation=landscape"
                alt="Why FormaCode"
                className="relative rounded-2xl w-full max-h-80 md:max-h-full object-cover border border-white/10"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-16 sm:py-24 px-4 sm:px-6 overflow-hidden">
        <div className="relative mx-auto max-w-4xl text-center reveal">
          <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold text-gradient">Ready to Build Something Exceptional?</h2>
          <p className="mt-4 text-white/70 text-base sm:text-lg">Let's discuss how FormaCode Technologies can transform your digital presence.</p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <Link to="/contact" className="btn-primary text-white font-semibold px-8 py-3.5 rounded-lg text-sm whitespace-nowrap inline-flex items-center justify-center gap-2">
              Start Your Project <i className="ri-rocket-2-line" />
            </Link>
            <Link to="/services" className="glass hover:border-neon-500 text-white font-semibold px-8 py-3.5 rounded-lg text-sm whitespace-nowrap inline-flex items-center justify-center gap-2 transition">
              Explore Services <i className="ri-compass-3-line" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}